<template>
  <el-select
    v-bind="selectAttrs"
    :value="value"
    multiple
    :popper-class="selectPopperClass"
    v-on="selectListeners"
    @change="handleChange"
  >
    <el-option
      v-if="selectableOptionValues.length > 0"
      class="select-all-option"
      :class="{ selected: isAllSelected }"
      :disabled="true"
      :label="computedSelectAllLabel"
      :value="SELECT_ALL_OPTION_VALUE"
      @click.native.stop.prevent="handleSelectAllClick"
    />
    <el-option
      v-for="option in options"
      :key="getOptionValue(option)"
      :label="getOptionLabel(option)"
      :value="getOptionValue(option)"
      :disabled="isOptionDisabled(option)"
    >
      <slot name="option" :option="option">
        <span>{{ getOptionLabel(option) }}</span>
      </slot>
    </el-option>
  </el-select>
</template>

<script>
import _ from 'lodash'

const SELECT_ALL_OPTION_VALUE = '__AJINGA_SELECT_ALL__'

export default {
  inheritAttrs: false,
  props: {
    value: {
      type: Array,
      default: () => []
    },
    options: {
      type: Array,
      default: () => []
    },
    labelKey: {
      type: String,
      default: 'label'
    },
    valueKey: {
      type: String,
      default: 'value'
    },
    disabledKey: {
      type: String,
      default: 'disabled'
    },
    selectAllLabel: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      SELECT_ALL_OPTION_VALUE
    }
  },
  computed: {
    selectAttrs () {
      return _.omit(this.$attrs, ['popper-class'])
    },
    selectListeners () {
      return _.omit(this.$listeners, ['input', 'change'])
    },
    selectPopperClass () {
      return _.compact([this.$attrs['popper-class'], 'select-all-el-select-dropdown']).join(' ')
    },
    computedSelectAllLabel () {
      return this.selectAllLabel || this.$t('common.label.selectAll')
    },
    selectableOptionValues () {
      return _.chain(this.options)
        .reject(option => this.isOptionDisabled(option))
        .map(option => this.getOptionValue(option))
        .value()
    },
    isAllSelected () {
      return this.selectableOptionValues.length > 0 && _.every(this.selectableOptionValues, optionValue => {
        return this.hasSelectedValue(optionValue, this.value)
      })
    }
  },
  methods: {
    getOptionLabel (option) {
      return _.get(option, this.labelKey)
    },
    getOptionValue (option) {
      return _.get(option, this.valueKey)
    },
    isOptionDisabled (option) {
      return Boolean(_.get(option, this.disabledKey))
    },
    hasSelectedValue (optionValue, selectedValues) {
      return _.some(selectedValues, selectedValue => _.isEqual(selectedValue, optionValue))
    },
    emitChange (value) {
      this.$emit('input', value)
      this.$emit('change', value)
    },
    handleChange (value) {
      this.emitChange(value)
    },
    handleSelectAllClick () {
      let value
      if (this.isAllSelected) {
        value = _.filter(this.value, selectedValue => {
          return !this.hasSelectedValue(selectedValue, this.selectableOptionValues)
        })
      } else {
        value = [...this.value]
        _.forEach(this.selectableOptionValues, optionValue => {
          if (!this.hasSelectedValue(optionValue, value)) {
            value.push(optionValue)
          }
        })
      }
      this.emitChange(value)
      this.$emit('select-all', !this.isAllSelected, value)
    }
  }
}
</script>

<style lang="scss">
.select-all-el-select-dropdown {
  .el-select-dropdown__item.select-all-option.is-disabled {
    color: #606266;
    cursor: pointer;

    &:hover {
      background-color: #f5f7fa;
    }

    &.selected {
      color: #0096cc;
      font-weight: 700;
    }
  }
}
</style>
